/**
 * Placements tab of a partnership page (booking, step 2).
 *
 * The campaign comes first: every placement type with brand slots in the
 * campaign shows up here as one card — what the partner sees (name,
 * description, price per slot), where it sits on lamptwist.com (placement
 * map) or an example picture when it isn't on the website, the measured
 * numbers, and the campaign's dated slots of that type.
 */
import { Link } from 'react-router-dom';
import { CommitInput } from '../ui/CommitInput';
import { Icon } from '../ui/Icon';
import { useToast } from '../ui/Toast';
import { ASSET_TYPE_ICON } from '../../lib/assetIcons';
import { AssetType } from '../../lib/types';
import { fmtShort } from '../../lib/dates';
import { CampaignMapPage, useCampaignMap } from '../../lib/api/placementMap';
import SectionStack from '../placements/SectionStack';
import MockupGallery from '../placements/MockupGallery';
import BookingOrder from '../assets/BookingOrder';
import { STAT_LABEL, fmtCount, toCount } from '../../lib/stats';
import CampaignPagesPanel from './CampaignPagesPanel';
import { reachLine, useReachSummary } from '../../lib/api/deals';
import { fmtEur, useRates } from '../../lib/api/rateCard';
import { useBulkSlots } from '../../lib/api/assets';
import { useQueryClient } from '@tanstack/react-query';
import axios from '../../utils/axiosInstance';
import {
  PartnershipPlacementRow,
  PartnershipSlot,
  usePartnershipPlacements,
  usePatchPartnershipPlacement,
} from '../../lib/api/partnerships';

const STYLES = `
.plc { display: grid; gap: 14px; }
.plc .intro { font-size: 12.5px; color: var(--ink-4); line-height: 1.5; margin: 0 0 4px; }
.plc .row { border-left: 3px solid var(--seasonal); padding: 16px 18px; }
.plc .row.off { border-left-color: var(--ink-6); }
.plc .head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 12px; }
.plc .head .name { flex: 1 1 260px; min-width: 0; }
.plc .head .name .input { font-size: 15px; font-weight: 600; }
.plc .type { font-size: 11.5px; color: var(--ink-4); display: inline-flex; align-items: center; gap: 5px; margin-top: 4px; }
.plc .taken { display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; background: #e6f4ea; color: #1e7a3c; white-space: nowrap; }
.plc .card.total { display: grid; gap: 4px; padding: 16px 20px; margin-bottom: 12px; background: #fff8e1; border-color: #f3dd94; }
.plc .card.total .lbl { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; color: #7a5a00; }
.plc .card.total .sum { font-size: 26px; font-weight: 700; color: var(--ink-1, #2b4138); }
.plc .card.total .sub { font-size: 12.5px; color: var(--ink-3); }
.plc .potential { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; background: #fff4cc; color: #7a5a00; white-space: nowrap; }
.plc .potential em { font-style: normal; font-weight: 500; opacity: .8; }
.plc .taken.full { background: var(--bg-subtle); color: var(--ink-4); }
.plc .seg { display: inline-flex; gap: 2px; padding: 2px; background: var(--bg-subtle); border: 1px solid var(--border); border-radius: 999px; }
.plc .seg button { border: 0; background: transparent; font: inherit; font-size: 12px; font-weight: 600; padding: 4px 12px; border-radius: 999px; color: var(--ink-3); cursor: pointer; }
.plc .seg button[aria-pressed="true"] { background: var(--bg-elevated); color: var(--ink); box-shadow: var(--shadow-sm); }
.plc .seg button.yes[aria-pressed="true"] { background: #E3F4E8; color: #1F6B3A; }
.plc .body { display: grid; grid-template-columns: minmax(0, 1.3fr) minmax(240px, 1fr); gap: 18px; }
@media (max-width: 900px) { .plc .body { grid-template-columns: 1fr; } }
.plc .lbl { font-size: 12.5px; font-weight: 500; color: var(--ink-3); margin-bottom: 5px; }
.plc .hint { font-size: 11.5px; color: var(--ink-4); line-height: 1.45; margin-top: 5px; }
.plc .price { position: relative; display: inline-block; }
.plc .stats { display: flex; gap: 14px; flex-wrap: wrap; }
.plc .stats label { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: var(--ink-3); }
.plc .stats .input { width: 120px; }
.plc .slotstats { display: inline-flex; gap: 4px; }
.plc .slotstats .input { width: 74px; padding: 3px 6px; font-size: 12px; text-align: right; }
.plc .price .input { width: 150px; padding-left: 24px; }
.plc .price.slot .input { width: 110px; padding: 3px 8px 3px 22px; font-size: 12.5px; text-align: right; }
.plc .price .cur { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); font-size: 12.5px; color: var(--ink-4); pointer-events: none; }
.plc .numbers { font-size: 12px; color: var(--ink-3); background: var(--bg-subtle); border-radius: var(--r-md); padding: 8px 10px; margin-top: 12px; }
.plc .where-k { font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--ink-4); font-weight: 600; margin-bottom: 6px; }
.plc .offsite { border: 1px dashed var(--border); border-radius: 8px; padding: 12px; font-size: 12px; color: var(--ink-4); display: grid; gap: 8px; }
.plc .slots { margin-top: 14px; border-top: 1px solid var(--border-soft); padding-top: 10px; }
.plc table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
.plc th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--ink-4); font-weight: 600; padding: 4px 8px; }
.plc td { padding: 6px 8px; border-top: 1px solid var(--border-soft); vertical-align: top; }
.plc td.num, .plc th.num { text-align: right; white-space: nowrap; }
.plc .own { font-size: 10.5px; font-weight: 600; color: var(--accent-3); margin-left: 4px; }
.plc .warn-line { font-size: 12px; color: #8A5A00; background: #FFF3D6; border-radius: var(--r-md); padding: 8px 10px; margin-bottom: 12px; line-height: 1.45; }
.plc .star { border: 0; background: none; cursor: pointer; font-size: 15px; color: var(--ink-6); padding: 0 4px; }
.plc .star.on { color: #e6a800; }
.plc .empty { padding: 28px 20px; text-align: center; font-size: 13px; color: var(--ink-4); }
`;

const slotDates = (s: PartnershipSlot) => {
  if (!s.publish_date) return 'No date';
  if (!s.end_date || s.end_date === s.publish_date) return fmtShort(s.publish_date);
  return `${fmtShort(s.publish_date)} – ${fmtShort(s.end_date)}`;
};

const toNum = (v: string) => {
  const t = v.trim().replace(',', '.');
  return t === '' ? null : t;
};

/** Yellow card: slots that don't say what they're sold as yet — pick it
 *  once for all of them (e.g. 4 newsletters → Dedicated newsletter). */
function SoldAsPicker({ row }: { row: PartnershipPlacementRow }) {
  const toast = useToast();
  const bulk = useBulkSlots();
  const { data: rates } = useRates();
  const choices = (rates ?? []).filter(
    (r) => r.active && (r.asset_type === row.asset_type || (r.pack_types ?? []).includes(row.asset_type)),
  );
  const pick = async (id: string) => {
    if (!id) return;
    try {
      await bulk.mutateAsync({ ids: row.slots.map((s) => s.id), sold_as: Number(id) });
      toast.push('Saved — the card now follows what you picked.');
    } catch {
      toast.push('Could not save — nothing changed.');
    }
  };
  return (
    <div className="warn-line" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <span style={{ flex: '1 1 260px' }}>
        {row.slots.length === 1 ? 'This slot doesn\'t' : `These ${row.slots.length} slots don't`} say what brands book yet.
      </span>
      {choices.length > 0 ? (
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          Sell {row.slots.length === 1 ? 'it' : 'all of them'} as
          <select className="input" defaultValue="" disabled={bulk.isPending} onChange={(e) => pick(e.target.value)} style={{ width: 'auto' }}>
            <option value="">— pick —</option>
            {choices.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </label>
      ) : (
        <span>Nothing on Placements &amp; prices sells a {row.asset_type} yet — tick it as Sellable there.</span>
      )}
    </div>
  );
}

/** What a card brings in if every slot sells at its price, own day prices
 *  included, and how many slots have no price yet (Doreen, 2026-10-26). */
function potentialOf(row: PartnershipPlacementRow) {
  let potential = 0;
  let unpriced = 0;
  for (const sl of row.slots) {
    const p = sl.price == null ? NaN : parseFloat(sl.price);
    if (Number.isFinite(p) && p > 0) potential += p * sl.brand_spots;
    else unpriced += sl.brand_spots;
  }
  return { potential, unpriced };
}

function PlacementCard({ row, campaignId, map }: { row: PartnershipPlacementRow; campaignId: string; map?: CampaignMapPage[] }) {
  const toast = useToast();
  const patch = usePatchPartnershipPlacement(campaignId);
  const reach = useReachSummary();
  const save = async (p: Parameters<typeof patch.mutateAsync>[0]) => {
    try {
      await patch.mutateAsync(p);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
      toast.push(detail ? `Could not save: ${Object.values(detail).flat().join(' ')}` : 'Could not save');
    }
  };
  // Where it sits on this campaign's version of the website pages (shown sections only).
  const key = row.rate?.key;
  const found = key
    ? (map ?? []).flatMap((p) => p.sections.filter((sec) => !sec.hidden).map((sec) => ({ page: p, sec })))
        .find(({ sec }) => sec.frames.some((f) => f.rate_key === key))
    : undefined;
  const hiddenOnly = !found && key
    ? (map ?? []).some((p) => p.sections.some((sec) => sec.frames.some((f) => f.rate_key === key)))
    : false;
  const numbers = reachLine(reach.data, row.asset_type, row.category);
  const full = row.spots_taken >= row.spots_total;
  const { potential, unpriced } = potentialOf(row);
  const qc = useQueryClient();
  // One slot's own price (empty = the card's price). Saved on the placement.
  const saveSlot = async (assetId: number, price: string | null) => patchSlot(assetId, { slot_price: price });
  const patchSlot = async (assetId: number, body: { slot_price?: string | null; highlighted?: boolean; slot_stat_1?: number | null; slot_stat_2?: number | null }) => {
    try {
      await axios.patch(`api/assets/${assetId}/`, body);
      qc.invalidateQueries({ queryKey: ['partnership', campaignId] });
      qc.invalidateQueries({ queryKey: ['assets'] });
    } catch {
      toast.push('Could not save that price.');
    }
  };
  const packTypes = row.rate?.pack_types ?? [];

  // Mockups: the campaign's own pictures, else the standard ones from Settings.
  const ownImages = row.example_images ?? [];
  const images = row.images ?? ownImages;
  const followsStandard = !ownImages.length && images.length > 0;

  return (
    <div className={`card row${row.on_sale ? '' : ' off'}`}>
      {row.needs_sold_as && <SoldAsPicker row={row} />}
      <div className="head">
        <div className="name">
          <CommitInput
            value={row.display_name}
            placeholder={row.asset_type}
            onCommit={(v) => save({ id: row.id, display_name: v.trim() })}
          />
          <div className="type">
            <Icon name={ASSET_TYPE_ICON[row.asset_type as AssetType] ?? 'tag'} size={11} />
            Placement in mosy: {[row.asset_type, ...packTypes].join(' + ')}
          </div>
        </div>
        <span className={`taken${full ? ' full' : ''}`}>
          <Icon name="user" size={11} />
          {row.spots_taken} / {row.spots_total} slots taken
        </span>
        <span
          className="potential"
          title="What this placement brings in if every slot is sold at its price (own day prices included)."
        >
          {potential > 0 ? `${fmtEur(potential)} if all sold` : 'No prices yet'}
          {potential > 0 && unpriced > 0 && <em> · {unpriced} without a price</em>}
        </span>
        <span className="seg">
          <button type="button" className="yes" aria-pressed={row.on_sale} onClick={() => !row.on_sale && save({ id: row.id, on_sale: true })}>For sale</button>
          <button type="button" aria-pressed={!row.on_sale} onClick={() => row.on_sale && save({ id: row.id, on_sale: false })}>Not for sale</button>
        </span>
      </div>

      <div className="body">
        <div>
          <div className="lbl">What partners read <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>· put **…** around words for bold</span></div>
          <CommitInput
            multiline
            rows={2}
            value={row.description}
            placeholder="One or two lines: what the brand gets."
            onCommit={(v) => save({ id: row.id, description: v })}
          />
          <div className="lbl" style={{ marginTop: 12 }}>What we need from you <span style={{ color: 'var(--ink-5)', fontWeight: 400 }}>(optional)</span></div>
          <CommitInput
            value={row.requirements}
            placeholder="e.g. You deliver the reel"
            onCommit={(v) => save({ id: row.id, requirements: v })}
          />
          <div className="lbl" style={{ marginTop: 12 }}>Price per slot</div>
          <span className="price">
            <span className="cur">€</span>
            <CommitInput
              type="number"
              min={0}
              value={row.price != null ? String(Number(row.price)) : ''}
              placeholder={row.standard_price != null ? `${Number(row.standard_price)} (standard)` : 'On request'}
              onCommit={(v) => save({ id: row.id, price: toNum(v) })}
            />
          </span>
          <div className="hint">
            {row.price != null
              ? 'Campaign price for every slot below — empty it to go back to the standard price.'
              : row.standard_price != null
                ? `Follows the standard price from Settings (${fmtEur(row.standard_price)}). Type a price to change it for this campaign.`
                : 'No standard price in Settings yet: brands see “On request”. Type a price for this campaign, or set one in Settings → Placements & prices.'}
            {' '}A single slot can have its own price in the table below.
          </div>
          <div className="lbl" style={{ marginTop: 12 }}>Expected per day</div>
          <div className="stats">
            {([0, 1] as const).map((i) => (
              <label key={i}>
                <CommitInput
                  type="number"
                  min={0}
                  value={(i === 0 ? row.stat_1 : row.stat_2) != null ? String(i === 0 ? row.stat_1 : row.stat_2) : ''}
                  placeholder={row.standard_stats?.[i] != null ? `${fmtCount(row.standard_stats[i])} (standard)` : '—'}
                  onCommit={(v) => save({ id: row.id, [i === 0 ? 'stat_1' : 'stat_2']: toCount(v) })}
                />
                <span>{STAT_LABEL[row.stat_labels?.[i] ?? 'page_views']}</span>
              </label>
            ))}
          </div>
          <div className="hint">
            {row.stat_1 != null || row.stat_2 != null
              ? 'This campaign\'s numbers (e.g. from last year) — empty a field to follow the standard.'
              : 'Follows the standard numbers from Settings. Type numbers for this campaign, e.g. last year\'s for Black Week.'}
            {' '}A single day can have its own numbers in the table below. Brands see them next to the price.
          </div>
          <div className="lbl" style={{ marginTop: 12 }}>Max per brand</div>
          <span className="price">
            <CommitInput
              type="number"
              min={1}
              value={row.max_per_brand != null ? String(row.max_per_brand) : ''}
              placeholder={row.standard_max != null ? `${row.standard_max} (standard)` : 'No maximum'}
              onCommit={(v) => save({ id: row.id, max_per_brand: v === '' ? null : Math.max(1, Math.floor(Number(v))) })}
            />
          </span>
          <div className="hint">
            {row.max_per_brand != null
              ? 'Most slots one brand may book on this campaign — empty it to follow the standard.'
              : row.standard_max != null
                ? `Follows the standard from Settings: one brand books at most ${row.standard_max}. Type a number to change it for this campaign.`
                : 'No maximum: one brand may book every slot. Type a number to set one for this campaign.'}
          </div>
          <div className="numbers">
            {numbers ?? 'No measured numbers for this placement yet.'}
          </div>
        </div>

        <div>
          <div className="where-k">Where brands see it</div>
          {found ? (
            <div>
              <div className="hint" style={{ marginTop: 0, marginBottom: 6 }}>{found.page.title} · {found.sec.label}</div>
              <SectionStack sections={[found.sec]} highlight={new Set([key as string])} onlyHighlighted />
            </div>
          ) : hiddenOnly ? (
            <div className="offsite">
              Its section is hidden on this campaign's website pages — show it under “Website pages” above.
            </div>
          ) : (
            <div className="offsite">
              <span>
                Not on the website — add mockup pictures (a newsletter, the post + story + reel…). Brands see them as
                their own tab next to the website pages.
              </span>
              <span className="hint" style={{ marginTop: 0 }}>
                {followsStandard
                  ? 'Standard pictures from Settings → Placements & prices. Change them here to use your own for this campaign.'
                  : ownImages.length
                    ? 'This campaign\'s own pictures.'
                    : 'No pictures yet — you can also add standard ones in Settings → Placements & prices.'}
                {ownImages.length > 0 && (row.standard_images?.length ?? 0) > 0 && (
                  <>
                    {' '}
                    <button type="button" className="link" style={{ fontSize: 12 }} onClick={() => save({ id: row.id, example_images: [] })}>
                      ↺ Default
                    </button>
                  </>
                )}
              </span>
              <MockupGallery images={images} onChange={(next) => save({ id: row.id, example_images: next })} />
            </div>
          )}
        </div>
      </div>

      <div className="slots">
        <table>
          <thead>
            <tr>
              <th>Dates</th>
              <th>Label</th>
              <th>Brands</th>
              <th className="num">Slots</th>
              <th className="num">Price</th>
              <th className="num" title="Expected per day — empty = the campaign's numbers">
                {STAT_LABEL[row.stat_labels?.[0] ?? 'page_views']} · {STAT_LABEL[row.stat_labels?.[1] ?? 'sessions'].toLowerCase()}
              </th>
              <th title="Stand out on the booking page (e.g. the pricier Friday)">★</th>
            </tr>
          </thead>
          <tbody>
            {row.slots.map((s) => (
              <tr key={s.id}>
                <td style={{ whiteSpace: 'nowrap' }}>{slotDates(s)}</td>
                <td className="muted" style={{ color: 'var(--ink-4)' }}>
                  {s.title || '—'}
                  {s.pack_parts.length > 0 && (
                    <span className="own" style={{ color: 'var(--ink-4)' }}>
                      + {s.pack_parts.map((x) => x.asset_type.replace('Instagram ', '').toLowerCase()).join(' + ')}
                    </span>
                  )}
                  {packTypes.length > 0 && s.pack_parts.length < packTypes.length && (
                    <span className="own" title="A placement of the pack is missing on this day">missing part</span>
                  )}
                </td>
                <td><BookingOrder order={s.booking_order ?? s.brands.map((name) => ({ brand_id: name, name, signed_up_at: null }))} /></td>
                <td className="num">{s.taken} / {s.brand_spots}</td>
                <td className="num">
                  <span className="price slot">
                    <span className="cur">€</span>
                    <CommitInput
                      type="number"
                      min={0}
                      value={s.slot_price != null ? String(Number(s.slot_price)) : ''}
                      placeholder={row.campaign_price != null ? String(Number(row.campaign_price)) : 'On request'}
                      onCommit={(v) => saveSlot(s.id, toNum(v))}
                    />
                  </span>
                  {s.slot_price != null && <span className="own" title="This slot has its own price">own price</span>}
                </td>
                <td className="num">
                  <span className="slotstats">
                    {([0, 1] as const).map((i) => (
                      <CommitInput
                        key={i}
                        type="number"
                        min={0}
                        value={(i === 0 ? s.slot_stat_1 : s.slot_stat_2) != null ? String(i === 0 ? s.slot_stat_1 : s.slot_stat_2) : ''}
                        placeholder={fmtCount(row.campaign_stats?.[i]) || '—'}
                        onCommit={(v) => patchSlot(s.id, { [i === 0 ? 'slot_stat_1' : 'slot_stat_2']: toCount(v) })}
                      />
                    ))}
                  </span>
                  {(s.slot_stat_1 != null || s.slot_stat_2 != null) && <span className="own" title="This day has its own numbers">own numbers</span>}
                </td>
                <td>
                  <button
                    type="button"
                    className={`star${s.highlighted ? ' on' : ''}`}
                    aria-pressed={s.highlighted}
                    title={s.highlighted ? 'Highlighted on the booking page — click to undo' : 'Highlight this slot on the booking page'}
                    onClick={() => patchSlot(s.id, { highlighted: !s.highlighted })}
                  >
                    ★
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="hint">
          Change the dates or the number of slots on the{' '}
          <Link to={`/campaigns/${campaignId}`}>campaign page</Link>.
        </div>
      </div>
    </div>
  );
}

export default function PlacementsPanel({ campaignId }: { campaignId: string }) {
  const { data: rows, isLoading, error } = usePartnershipPlacements(campaignId);
  const map = useCampaignMap(campaignId);
  const forSale = new Set((rows ?? []).filter((r) => r.on_sale && r.rate).map((r) => r.rate!.key));
  // Campaign total: the cards for sale, if every slot sells.
  const selling = (rows ?? []).filter((r) => r.on_sale);
  const total = selling.reduce(
    (acc, r) => { const p = potentialOf(r); return { potential: acc.potential + p.potential, unpriced: acc.unpriced + p.unpriced }; },
    { potential: 0, unpriced: 0 },
  );
  const slotCount = selling.reduce((n, r) => n + r.spots_total, 0);
  const notSelling = (rows ?? []).length - selling.length;
  return (
    <div className="plc">
      <style>{STYLES}</style>
      <p className="intro">
        Everything brands can book on this page, read from the campaign's placements with brand slots — one card per
        thing you sell (what the placements are “sold as”). Set what partners read and the price per slot here; add or change slots on the{' '}
        <Link to={`/campaigns/${campaignId}`}>campaign page</Link>.
      </p>
      {isLoading ? (
        <div className="card empty">Loading placements…</div>
      ) : error ? (
        <div className="card empty">Could not load the placements.</div>
      ) : !rows?.length ? (
        <div className="card empty">
          No placements with brand slots yet. Open a placement on the campaign page and fill in "Brand slots" — it shows up here.
        </div>
      ) : (
        <>
          <div className="card total">
            <div className="lbl">If every slot sells</div>
            <div className="sum">{total.potential > 0 ? fmtEur(total.potential) : 'No prices yet'}</div>
            <div className="sub">
              {slotCount} slot{slotCount === 1 ? '' : 's'} on {selling.length} placement{selling.length === 1 ? '' : 's'} for sale
              {total.unpriced > 0 && <> · <b>{total.unpriced} without a price</b> (not counted)</>}
              {notSelling > 0 && <> · {notSelling} “Not for sale” placement{notSelling === 1 ? '' : 's'} left out</>}
            </div>
          </div>
          {rows.map((r) => <PlacementCard key={r.id} row={r} campaignId={campaignId} map={map.data} />)}
        </>
      )}
      <CampaignPagesPanel campaignId={campaignId} highlight={forSale} />
    </div>
  );
}
